import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';

export interface ConfirmDialogOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    danger?: boolean;
    /** Show top-right X + Escape to dismiss without choosing confirm/cancel (resolves null). Default true. */
    dismissible?: boolean;
    /** Optional footer button that also dismisses (resolves null). Use for "Stay on Workbench". */
    dismissText?: string;
}

const dialogCloseBtnClass =
    'inline-flex items-center justify-center z-10 p-2 rounded-full border border-border bg-background text-foreground shadow-md transition-all cursor-pointer active:scale-[0.98] hover:bg-muted hover:border-primary/50';

interface ConfirmDialogPanelProps {
    options: ConfirmDialogOptions;
    onClose: (value: boolean | null) => void;
}

/** Presentational confirm dialog — used by useConfirmDialog and tests. */
export function ConfirmDialogPanel({ options, onClose }: ConfirmDialogPanelProps) {
    const dismissible = options.dismissible !== false;
    // Three-action layout (Stay | Save | Leave) puts confirm in the middle.
    // Two-action layout keeps Cancel | Confirm.
    const confirmBeforeCancel = Boolean(options.dismissText);

    const cancelBtn = (
        <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted"
        >
            {options.cancelText || 'Cancel'}
        </button>
    );
    const confirmBtn = (
        <button
            type="button"
            onClick={() => onClose(true)}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${options.danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-primary text-primary-foreground hover:bg-brand-gold-hover'}`}
        >
            {options.confirmText || 'Confirm'}
        </button>
    );

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            role="presentation"
        >
            <div
                className="relative w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-dialog-title"
            >
                {dismissible && (
                    <button
                        type="button"
                        onClick={() => onClose(null)}
                        className={`absolute top-3 right-3 ${dialogCloseBtnClass}`}
                        aria-label="Close dialog"
                        title="Stay on this page"
                        data-testid="confirm-dialog-close"
                    >
                        <X className="w-5 h-5 text-foreground stroke-[2.5]" aria-hidden="true" />
                    </button>
                )}
                <h3 id="confirm-dialog-title" className="text-base font-semibold mb-2 pr-10">{options.title}</h3>
                <p className="text-sm text-muted-foreground mb-5 whitespace-pre-line">{options.message}</p>
                <div className="flex flex-wrap justify-end gap-2">
                    {dismissible && options.dismissText && (
                        <button
                            type="button"
                            onClick={() => onClose(null)}
                            className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted"
                            data-testid="confirm-dialog-dismiss"
                        >
                            {options.dismissText}
                        </button>
                    )}
                    {confirmBeforeCancel ? (
                        <>
                            {confirmBtn}
                            {cancelBtn}
                        </>
                    ) : (
                        <>
                            {cancelBtn}
                            {confirmBtn}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export interface PromptDialogOptions {
    title: string;
    message?: string;
    placeholder?: string;
    defaultValue?: string;
    confirmText?: string;
    cancelText?: string;
    danger?: boolean;
}

export function useConfirmDialog() {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmDialogOptions | null>(null);
    const [resolver, setResolver] = useState<((value: boolean | null) => void) | null>(null);

    const confirm = (opts: ConfirmDialogOptions): Promise<boolean | null> => {
        setOptions(opts);
        setOpen(true);
        return new Promise<boolean | null>((resolve) => {
            setResolver(() => resolve);
        });
    };

    const close = useCallback((value: boolean | null) => {
        setOpen(false);
        setResolver((current: ((value: boolean | null) => void) | null) => {
            current?.(value);
            return null;
        });
        setOptions(null);
    }, []);

    useEffect(() => {
        if (!open || options?.dismissible === false) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, options?.dismissible, close]);

    const dialog = open && options ? (
        <ConfirmDialogPanel options={options} onClose={close} />
    ) : null;

    return { confirm, confirmDialog: dialog };
}

export function usePromptDialog() {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<PromptDialogOptions | null>(null);
    const [value, setValue] = useState('');
    const [resolver, setResolver] = useState<((value: string | null) => void) | null>(null);

    const prompt = (opts: PromptDialogOptions): Promise<string | null> => {
        setOptions(opts);
        setValue(opts.defaultValue || '');
        setOpen(true);
        return new Promise<string | null>((resolve) => {
            setResolver(() => resolve);
        });
    };

    const close = (next: string | null) => {
        setOpen(false);
        resolver?.(next);
        setResolver(null);
        setValue('');
    };

    const dialog = open && options ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div
                className="relative w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="prompt-dialog-title"
            >
                <button
                    type="button"
                    onClick={() => close(null)}
                    className={`absolute top-3 right-3 ${dialogCloseBtnClass}`}
                    aria-label="Close dialog"
                    data-testid="prompt-dialog-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <h3 id="prompt-dialog-title" className="text-base font-semibold mb-2 pr-8">{options.title}</h3>
                {options.message && <p className="text-sm text-muted-foreground mb-3 whitespace-pre-line">{options.message}</p>}
                <input
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={options.placeholder || 'Enter value'}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') close(value.trim() ? value.trim() : null);
                        if (e.key === 'Escape') close(null);
                    }}
                    className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md mb-5"
                />
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => close(null)}
                        className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                        {options.cancelText || 'Cancel'}
                    </button>
                    <button
                        type="button"
                        onClick={() => close(value.trim() ? value.trim() : null)}
                        className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${options.danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-primary text-primary-foreground hover:bg-brand-gold-hover'}`}
                    >
                        {options.confirmText || 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return { prompt, promptDialog: dialog };
}
